import { useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import useI18n from '@/shared/hooks/useI18n'
import { Button, GenericDialog, NumericInputGroup } from '@/common'
import { CheckCircleIcon } from '@/common/components/icons'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export default function EditActivityLevelDialog({
  isOpen,
  activityLevel,
  close,
  reportId,
  methodEmissionReportId
}: {
  isOpen: boolean
  activityLevel?: number | null
  close: () => void
  reportId: string
  methodEmissionReportId: string
}) {
  const { t } = useI18n()
  const navigate = useNavigate()

  const isInitialActivityLevelInput = !activityLevel

  const { periodId } = useParams<{ periodId: string }>()
  const { mutateAsync: updateMethodEmissionReport, isPending: isUpdatingEmissionReport } =
    useUpdateMethodEmissionReport(reportId, methodEmissionReportId)
  const [updatedActivityLevel, setUpdatedActivityLevel] = useState(activityLevel)
  const isFormValid =
    updatedActivityLevel !== undefined && updatedActivityLevel !== null && updatedActivityLevel > 0

  useEffect(() => {
    setUpdatedActivityLevel(activityLevel)
  }, [activityLevel])

  const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    if (isFormValid) {
      await updateMethodEmissionReport({
        activityLevel: updatedActivityLevel
      })
      close()
    }
  }

  const onWindowClose = () => {
    if (isInitialActivityLevelInput) {
      return
    }
    close()
  }

  return (
    <GenericDialog
      size="lg"
      title={
        isInitialActivityLevelInput
          ? t('report:editActivityLevelDialog.firstTime.title')
          : t('report:editActivityLevelDialog.edit.title')
      }
      subtitle={
        isInitialActivityLevelInput
          ? t('report:editActivityLevelDialog.firstTime.subtitle')
          : t('report:editActivityLevelDialog.edit.subtitle')
      }
      icon={<CheckCircleIcon className="size-6 text-green-600" />}
      isOpen={isOpen}
      close={onWindowClose}
    >
      <form onSubmit={onSubmit}>
        <div className="space-y-4">
          <NumericInputGroup
            label={t('report:editActivityLevelDialog.fields.activityLevel.label')}
            tooltip={t('report:editActivityLevelDialog.fields.activityLevel.tooltip')}
            value={updatedActivityLevel?.toString() || ''}
            onChange={e => setUpdatedActivityLevel(Number(e.target.value))}
            min={0}
          />
          <footer className="gap-2 flex justify-end">
            {isInitialActivityLevelInput ? (
              <Button type="button" variant="neutral" onClick={() => navigate(`../${periodId}`)}>
                {t('report:editActivityLevelDialog.firstTime.backToReportsPage')}
              </Button>
            ) : (
              <Button type="button" variant="neutral" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
            )}
            <Button type="submit" disabled={!isFormValid} isLoading={isUpdatingEmissionReport}>
              {t('common:actions.confirm')}
            </Button>
          </footer>
        </div>
      </form>
    </GenericDialog>
  )
}
